import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import { getStoredToken, setStoredToken } from '@/api/client'
import {
  login as apiLogin,
  loginTwoFactor as apiLoginTwoFactor,
  logout as apiLogout,
  getProfile,
  isTokenExpired,
  getRememberMe,
  setStoredTokenExpiry,
} from '@/api/auth'
import { getPortalConfig } from '@/api/portal'
import {
  getCurrentSlug,
  hasLegacyProviderToken,
  adoptLegacyProviderToken,
} from '@/utils/portalSlug'
import { isTwoFactorChallenge } from '@/api/types'
import type {
  ProfileResponse,
  LoginRequest,
  PendingTwoFactorLogin,
} from '@/api/types'

/* ==========================================================================
   Auth Context
   Provides authentication state and methods throughout the app
   ========================================================================== */

export interface AuthState {
  /** The authenticated user's profile, null if not authenticated */
  user: ProfileResponse | null
  /** Whether authentication is currently being checked */
  loading: boolean
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Error message from the last auth operation */
  error: string | null
  /** Set when the password was right but an authenticator code is still needed
   *  (PORTAL-187). The login page then shows the code step. */
  twoFactorPending: PendingTwoFactorLogin | null
}

/** How a password login ended. `two_factor_required`: the password was right and
 *  `twoFactorPending` now holds the challenge for the code step. */
export type LoginOutcome = 'signed_in' | 'two_factor_required' | 'failed'

interface AuthContextValue extends AuthState {
  /** Login with email and password. On `failed`, `error` holds the reason. */
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<LoginOutcome>
  /** Finish a pending login with the authenticator code. */
  verifyTwoFactor: (code: string, rememberDevice: boolean) => Promise<boolean>
  /** Abandon a pending login and return to the password form. */
  cancelTwoFactor: () => void
  /** Login with an existing token (used after accepting invitation) */
  loginWithToken: (token: string, expiresAt: string) => Promise<boolean>
  /** Logout the current user */
  logout: () => Promise<void>
  /** Refresh the user's profile from the API */
  refreshProfile: () => Promise<void>
  /** Clear any auth error */
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export interface AuthProviderProps {
  children: ReactNode
}

/**
 * Auth provider that manages authentication state.
 * Wrap your app with this provider to enable authentication.
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [twoFactorPending, setTwoFactorPending] =
    useState<PendingTwoFactorLogin | null>(null)

  // Derived state
  const isAuthenticated = !!user

  /**
   * Check for existing session on mount.
   */
  useEffect(() => {
    async function checkAuth() {
      let token = getStoredToken()

      // Deploy-time legacy session: before tokens were namespaced per slug they
      // lived under a bare key. Such a token carries a pharmacy_id but no slug,
      // so we adopt it into this portal's per-slug key only after confirming (via
      // the portal's pharmacy id) that it belongs here — never another portal's
      // token. This restores already-logged-in providers without a forced
      // re-login, and leaves a non-matching token for its real portal to adopt.
      if (!token && hasLegacyProviderToken()) {
        const slug = getCurrentSlug()
        if (slug) {
          try {
            const config = await getPortalConfig(slug)
            token = adoptLegacyProviderToken(slug, config.id)
          } catch {
            // Portal config unavailable — skip adoption, treat as no session.
          }
        }
      }

      // No token stored
      if (!token) {
        setLoading(false)
        return
      }

      // Token is expired
      if (isTokenExpired()) {
        // If "remember me" was not checked, clear the expired token
        if (!getRememberMe()) {
          await apiLogout()
        }
        setLoading(false)
        return
      }

      // Token exists and not expired - fetch profile
      try {
        const profile = await getProfile()
        setUser(profile)
      } catch {
        // Token is invalid or expired - clear it
        await apiLogout()
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  /**
   * Login with email and password.
   * Returns true on success, false on failure (sets error state).
   */
  const login = useCallback(
    async (
      email: string,
      password: string,
      rememberMe = false
    ): Promise<LoginOutcome> => {
      setError(null)
      setLoading(true)

      try {
        const request: LoginRequest = {
          email,
          password,
          remember_me: rememberMe,
        }

        const result = await apiLogin(request)
        if (isTwoFactorChallenge(result)) {
          setTwoFactorPending({
            token: result.two_factor_token,
            email,
            rememberMe,
          })
          return 'two_factor_required'
        }

        // Fetch user profile after successful login
        const profile = await getProfile()
        setUser(profile)

        return 'signed_in'
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Login failed. Please check your credentials.'
        setError(message)
        return 'failed'
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Finish a pending 2FA login with the code from the authenticator app.
   * Returns true on success, false on failure (sets error state). An expired
   * challenge drops back to the password form.
   */
  const verifyTwoFactor = useCallback(
    async (code: string, rememberDevice: boolean): Promise<boolean> => {
      if (!twoFactorPending) return false
      setError(null)
      setLoading(true)

      try {
        await apiLoginTwoFactor(
          {
            two_factor_token: twoFactorPending.token,
            code,
            remember_device: rememberDevice,
          },
          twoFactorPending.email,
          twoFactorPending.rememberMe
        )
        const profile = await getProfile()
        setUser(profile)
        setTwoFactorPending(null)
        return true
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Verification failed. Please try again.'
        if (message.includes('expired')) {
          setTwoFactorPending(null)
        }
        setError(message)
        return false
      } finally {
        setLoading(false)
      }
    },
    [twoFactorPending]
  )

  const cancelTwoFactor = useCallback(() => {
    setTwoFactorPending(null)
    setError(null)
  }, [])

  /**
   * Login with an existing token (used after accepting invitation or other flows).
   * Returns true on success, false on failure (sets error state).
   */
  const loginWithToken = useCallback(
    async (token: string, expiresAt: string): Promise<boolean> => {
      setError(null)
      setLoading(true)

      try {
        // Store the token
        setStoredToken(token)
        setStoredTokenExpiry(expiresAt)

        // Fetch user profile
        const profile = await getProfile()
        setUser(profile)

        return true
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Login failed. Please try again.'
        setError(message)
        return false
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Logout the current user.
   */
  const logout = useCallback(async () => {
    setLoading(true)
    try {
      await apiLogout()
    } finally {
      setUser(null)
      setError(null)
      setLoading(false)
    }
  }, [])

  /**
   * Refresh the user's profile from the API.
   */
  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      const profile = await getProfile()
      setUser(profile)
    } catch {
      // If profile fetch fails, user is no longer authenticated
      await logout()
    }
  }, [isAuthenticated, logout])

  /**
   * Clear any auth error.
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated,
    error,
    twoFactorPending,
    login,
    verifyTwoFactor,
    cancelTwoFactor,
    loginWithToken,
    logout,
    refreshProfile,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth state and methods from any component.
 * Must be used within an AuthProvider.
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const { login, error, loading } = useAuthContext()
 *
 *   const handleSubmit = async (e: FormEvent) => {
 *     e.preventDefault()
 *     const outcome = await login(email, password, rememberMe)
 *     if (outcome === 'signed_in') {
 *       navigate('/')
 *     }
 *   }
 *
 *   return <form onSubmit={handleSubmit}>...</form>
 * }
 * ```
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

export default AuthProvider
